import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { listPhotographs, submitContact } from "../lib/api";
import Seo from "../components/Seo";

/*
 * Contact — a quiet route for high-resolution print requests. No sales,
 * no download. Pre-fills the photograph when arriving from Explore
 * (?photo=p14 or router state.photoId).
 */
export default function Contact() {
  const [photos, setPhotos] = useState([]);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const preselectedFromUrl = searchParams.get("photo") || location.state?.photoId || "";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [photographId, setPhotographId] = useState(preselectedFromUrl);
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ ok: null, error: null });

  useEffect(() => {
    listPhotographs().then(setPhotos);
  }, []);

  useEffect(() => {
    if (preselectedFromUrl && !photographId) setPhotographId(preselectedFromUrl);
  }, [preselectedFromUrl, photographId]);

  const photographLabel = useMemo(() => {
    const p = photos.find((x) => x.id === photographId);
    return p ? `${p.id} · ${p.title || "Untitled"}` : "";
  }, [photos, photographId]);

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setStatus({ ok: null, error: null });
    if (!name.trim() || !email.trim() || !message.trim() || message.trim().length < 10) {
      setStatus({ ok: null, error: "Please complete every field. Message must be at least 10 characters." });
      return;
    }
    setSubmitting(true);
    try {
      await submitContact({
        name: name.trim(),
        email: email.trim(),
        photograph_id: photographId || null,
        message: message.trim(),
        website,
      });
      setStatus({ ok: true, error: null });
      setName("");
      setEmail("");
      setMessage("");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again in a moment.";
      setStatus({ ok: null, error: typeof detail === "string" ? detail : "Please try again." });
    } finally {
      setSubmitting(false);
    }
  }

  if (status.ok) {
    return (
      <main
        data-testid="contact-page"
        className="min-h-screen bg-[color:var(--bg)] text-white pt-32 pb-24 grain"
      >
        <Seo title="Contact" path="/contact" />
        <div className="max-w-xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.2, 0.7, 0.1, 1] }}
            data-testid="contact-success"
          >
            <p className="font-serif-display italic text-2xl md:text-3xl text-white/85 leading-snug">
              Thank you.
            </p>
            <p className="mt-4 font-serif-display text-white/60 text-base md:text-lg leading-relaxed">
              Your message has been sent. If a reply is warranted, you will hear
              back at the address you provided.
            </p>
            <div className="mt-10 flex gap-6 text-sm font-serif-display italic">
              <Link to="/gallery" className="text-white/70 hover:text-white nav-link" data-testid="contact-back-gallery">
                Return to Gallery
              </Link>
              <button
                type="button"
                onClick={() => setStatus({ ok: null, error: null })}
                className="text-white/50 hover:text-white nav-link"
                data-testid="contact-send-another"
              >
                Send another
              </button>
            </div>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="contact-page"
      className="min-h-screen bg-[color:var(--bg)] text-white pt-32 pb-24 grain"
    >
      <Seo
        title="Contact"
        description="Contact Carol Lines about a high-resolution copy of one of the photographs for personal printing."
        path="/contact"
      />
      <div className="max-w-xl mx-auto px-6 md:px-10">
        <header className="mb-10">
          <h1
            data-testid="contact-heading"
            className="font-serif-display font-light text-3xl md:text-4xl leading-tight tracking-tight text-white/85"
          >
            Contact
          </h1>
          <p className="mt-4 font-serif-display text-white/70 text-base md:text-lg leading-relaxed">
            If you would like a high-resolution copy of one of the photographs
            for personal printing, you can request one here.
          </p>
        </header>

        <form onSubmit={onSubmit} className="space-y-8" data-testid="contact-form" noValidate>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.24em] text-white/45 font-serif-display italic mb-2">
              Name
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              data-testid="contact-name"
              className="q-input"
            />
          </label>

          <label className="block">
            <span className="block text-xs uppercase tracking-[0.24em] text-white/45 font-serif-display italic mb-2">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="contact-email"
              className="q-input"
            />
          </label>

          <label className="block">
            <span className="block text-xs uppercase tracking-[0.24em] text-white/45 font-serif-display italic mb-2">
              Photograph {photographLabel && <span className="normal-case tracking-normal text-white/65 ml-2">— {photographLabel}</span>}
            </span>
            <select
              value={photographId}
              onChange={(e) => setPhotographId(e.target.value)}
              data-testid="contact-photograph"
              className="q-input appearance-none"
              style={{ backgroundColor: "transparent" }}
            >
              <option value="" style={{ background: "#0b0c0e" }}>
                — no specific photograph —
              </option>
              {photos.map((p) => (
                <option key={p.id} value={p.id} style={{ background: "#0b0c0e" }}>
                  {p.id} · {p.title || "Untitled"}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-xs uppercase tracking-[0.24em] text-white/45 font-serif-display italic mb-2">
              Message / intended use
            </span>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              minLength={10}
              maxLength={4000}
              data-testid="contact-message"
              className="q-input"
              style={{ resize: "vertical" }}
            />
          </label>

          {/* honeypot — hidden from users, expected to stay empty */}
          <label
            aria-hidden="true"
            tabIndex={-1}
            style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}
          >
            Website
            <input
              type="text"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              autoComplete="off"
              tabIndex={-1}
            />
          </label>

          {status.error && (
            <p data-testid="contact-error" className="text-sm text-red-300/80 font-serif-display italic">
              {status.error}
            </p>
          )}

          <div className="flex items-center justify-between pt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="btn-ghost disabled:opacity-40"
            >
              {submitting ? "Sending…" : "Send request"}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-sm font-serif-display italic text-white/45 hover:text-white/80"
              data-testid="contact-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
